import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Box, Typography, Paper, useTheme } from '@mui/material';

const getChartOptions = (valueLabel, isDarkMode) => {
  const textColor = isDarkMode ? '#ffffff' : '#1f293b';
  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)';

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: textColor,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: isDarkMode ? '#333333' : '#ffffff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: isDarkMode ? '#4d4d4d' : '#e2e8f0',
        borderWidth: 1,
        filter: (tooltipItem) => tooltipItem.parsed.y > 0,
        callbacks: {
          footer: (tooltipItems) => {
            const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Date',
          color: textColor,
        },
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: valueLabel,
          color: textColor,
        },
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
    },
  };
};

const PerformerChart = ({ performer, chartData, rangeLabel, subtitle, valueLabel }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const total = useMemo(() => {
    if (!chartData?.datasets?.length) return 0;
    return chartData.datasets.reduce(
      (sum, dataset) => sum + dataset.data.reduce((daySum, value) => daySum + value, 0),
      0
    );
  }, [chartData]);

  return (
    <Paper sx={{ p: 3, mb: 3, color: 'text.primary' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
        <Typography variant="h6">{performer.label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {total} {valueLabel.toLowerCase()} in selected period
        </Typography>
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
      <Box sx={{ height: 360 }}>
        {chartData.labels.length > 0 ? (
          <Bar data={chartData} options={getChartOptions(valueLabel, isDarkMode)} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary">
              No data for {rangeLabel.toLowerCase()}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PerformerChart;
